// Запись готовой партии для разбора: сид + конфигурация игроков + журнал действий (партия воспроизводится с нуля)
// + журнал текстом и итог. Хотсит-клиент копит записи и отсылает на сервер в фоне, онлайн-партии записывает сервер.
import type { Game, GameRecord } from '../types.ts';
import { LEGACY_PACKAGE } from './rebalance.ts';
import { RULES_VERSION, rv } from './rules.ts';
import { applyEntry, gameFlags } from './game.ts';
import { replayJournal, candidateVersions } from './game.ts';
import { applyRebalance, cloneGame } from './state.ts';
import type { ReplaySpec } from './game.ts';

/** clients — id клиентов по местам (онлайн: сервер знает, кто из людей за каким местом); пустая строка — бот/неизвестно */
export function gameRecord(G: Game, kind: GameRecord['kind'], client: string, agent?: string, clients?: readonly string[]): GameRecord {
  return {
    v: 2,
    kind,
    client,
    finished: new Date().toISOString(),
    seed: G.seed ?? 0,
    players: G.players.map((p, i) => ({ name: p.name, bot: p.bot, ...(p.botLevel ? { botLevel: p.botLevel } : {}), ...(p.botStyle ? { botStyle: p.botStyle } : {}), ...(p.boss ? { boss: p.boss } : {}), ...(clients?.[i] ? { client: clients[i] } : {}) })),
    ...gameFlags(G),
    ...(G.daily ? { daily: G.daily } : {}),
    rulesVersion: rv(G),
    journal: G.journal ?? [],
    log: G.log,
    winner: G.winner,
    ...(G.winners ? { winners: G.winners } : {}),
    scores: G.scores ?? null,
    turnNo: G.turnNo ?? 0,
    ...(agent ? { agent } : {}),
  };
}

/**
 * Игроки записи с уровнями ботов в нынешней семантике: в записях v1 'easy' означал полный честный мозг
 * (нынешний 'hard'), а 'hard' — подглядывающего (нынешний 'cheat'). Для реплея журнала уровень не важен,
 * но инструменты (games.ts --stop, branches.ts) зовут decide() с уровнем игрока — семантика должна совпадать.
 */
export function recPlayers(rec: Pick<GameRecord, 'players'> & { v?: number }): GameRecord['players'] {
  if ((rec.v ?? 1) >= 2) return rec.players;
  const map = { easy: 'hard', hard: 'cheat' } as const;
  return rec.players.map((p) => (p.botLevel && p.botLevel in map ? { ...p, botLevel: map[p.botLevel as keyof typeof map] } : p));
}

type Rec = Pick<GameRecord, 'seed' | 'players' | 'journal'> & Partial<Pick<GameRecord, 'rebalance' | 'mutators' | 'rebalanceVersion' | 'rulesVersion' | 'winner' | 'scores'>> & { v?: number };

/**
 * Версии правил, на которых стоит пробовать воспроизвести запись, от самой вероятной: записанная версия,
 * затем версия по старому номеру пакета режима, затем все остальные от новой к старой.
 */
export function recordVersions(rec: Rec): number[] {
  const first: number[] = [];
  if (rec.rulesVersion != null) first.push(rec.rulesVersion);
  const legacy = rec.rebalanceVersion != null ? LEGACY_PACKAGE[rec.rebalanceVersion] : undefined;
  if (legacy != null) first.push(legacy, legacy + 1);
  const rest = candidateVersions(!!rec.rebalance).filter((v) => !first.includes(v));
  return [...first, ...rest];
}

/** Итог реплея совпал с записью: партия окончена тем же победителем с теми же очками */
export function matchesOutcome(G: Game, rec: Rec): boolean {
  if (rec.scores == null) return true; // запись недоигранной партии (нет очков) — сверять нечего
  return G.phase === 'ended' && G.winner === rec.winner && JSON.stringify(G.scores) === JSON.stringify(rec.scores);
}

export interface ReplayOutcome {
  G: Game;
  /** версия правил, на которой запись воспроизвелась (или на которой реплей ушёл дальше всего) */
  version: number;
  ok: boolean;
  /** первая запись журнала, которая не легла (при ok — −1) */
  failAt: number;
  error: string;
  /** партия до появления версий, продолженная после обновления: до записи №at шла версия from, дальше — version */
  switched?: { at: number; from: number };
}

/**
 * Воспроизвести партию по записи на её версии правил. Версия записана — реплей ею; не записана (старые записи) —
 * подбор: журнал проигрывается на версиях от вероятной к менее вероятным, и подходит та, на которой он лёг
 * целиком и итог партии совпал с записанным. until — воспроизвести только до записи №until (не включая);
 * версия при этом всё равно подбирается по полному журналу.
 */
export function replayRecordOutcome(rec: Rec, until = Infinity): ReplayOutcome {
  const spec: ReplaySpec = { seed: rec.seed, players: recPlayers(rec), ...gameFlags(rec), journal: rec.journal };
  const versions = rec.rulesVersion != null ? [rec.rulesVersion] : recordVersions(rec);
  let best: ReplayOutcome | null = null;
  for (const v of versions) {
    const r = replayJournal(spec, v);
    const ok = r.failAt < 0 && matchesOutcome(r.G, rec);
    const out: ReplayOutcome = { G: r.G, version: v, ok, failAt: r.failAt, error: r.failAt < 0 && !ok ? 'итог партии не совпал с записью' : r.error };
    if (ok) {
      if (until < rec.journal.length) out.G = replayJournal(spec, v, until).G;
      return out;
    }
    if (!best || (out.failAt < 0 ? rec.journal.length : out.failAt) > (best.failAt < 0 ? rec.journal.length : best.failAt)) best = out;
  }
  if (!best) throw new Error('нет версий для реплея');
  // партия до появления версий могла быть продолжена после обновления: правила сменились посреди журнала.
  // Пробуем одну смену — в точке, где лучшая версия споткнулась: до неё та версия, после — каждая другая
  const sw = switchedReplay(spec, rec, best, versions);
  if (sw) {
    if (until < rec.journal.length) sw.G = switchedReplay(spec, rec, best, versions, until)?.G ?? sw.G;
    return sw;
  }
  return best;
}

function switchedReplay(spec: ReplaySpec, rec: Rec, best: ReplayOutcome, versions: number[], until = Infinity): ReplayOutcome | null {
  const at = best.failAt;
  if (at <= 0) return null;
  const head = replayJournal(spec, best.version, Math.min(at, until));
  if (head.failAt >= 0) return null;
  if (until <= at) return { ...best, G: head.G, ok: true, failAt: -1, error: '' };
  for (const v of versions) {
    if (v === best.version) continue;
    const G = cloneGame(head.G);
    G.rulesVersion = v;
    applyRebalance(!!G.rebalance, v);
    let failAt = -1;
    let error = '';
    const n = Math.min(until, rec.journal.length);
    for (let i = at; i < n; i++) {
      const e = rec.journal[i]!;
      const r = applyEntry(G, e);
      if (!r.ok) { failAt = i; error = r.error; break; }
    }
    if (failAt < 0 && matchesOutcome(G, rec)) return { G, version: v, ok: true, failAt: -1, error, switched: { at, from: best.version } };
  }
  return null;
}

/**
 * Воспроизвести партию по записи: createGame с тем же сидом и игроками + журнал до записи `until` (не включая).
 * Запись, которая не воспроизводится ни одной версией правил, — расхождение движка с записью: бросает ошибку
 * с номером записи и версией, на которой реплей ушёл дальше всего.
 */
export function replayRecord(rec: Rec, until = Infinity): Game {
  const r = replayRecordOutcome(rec, until);
  if (!r.ok) {
    const where = r.failAt < 0 ? 'конец журнала' : `запись ${r.failAt} (${rec.journal[r.failAt]?.a.name} игрока ${rec.journal[r.failAt]?.pid})`;
    throw new Error(`партия не воспроизводится ни одной версией правил (лучшая v${r.version} из ${RULES_VERSION}): ${where}: ${r.error}`);
  }
  return r.G;
}
